import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {toast} from 'sonner';
import {Eye, Pencil, Plus, Trash2, Upload} from 'lucide-react';
import {DiibsIcon} from '../../../components/common/DiibsIcon';
import { PageLink, PageTitle } from '../../../../_metronic/layout/core';
import { useThemeMode } from '../../../../_metronic/partials';
import DataTableWrapper from '../../../DataTable';
import {
	ADMIN_ROUTE,
	API_ERROR_MESSAGE,
	TOAST_GENERAL,
	USERS_ROUTE,
	convertUtcToLocal,
} from '../../../Helpers';
import { CustomModal } from '../../../components/Modal';
import StatusSelectBox from '../../../components/Sections/Admin/StatusSelectBox';
import { CommonRowInTable, CommonViewInHeader } from '../../../components/common/DataTableViews';
import { useSessionReducer } from '../../../hook/useSessionReducer';
import { useAuth } from '../../../modules/auth';
import reducerFunc from './action';
import { TInitialState } from './index.d';

interface IParamsInfo {
	searchKeyword?: string;
	statusFilter?: string;
	verifiedFilter?: string;
	startDate?: string;
	endDate?: string;
}

const initialState: TInitialState = {
	loading: true,
	data: [],
	totalData: -1,
	isModalOpen: false,
	modalType: 'USER',
	selectedRow: undefined,
	currentPage: 1,
	totalPages: 1,
	error: null,
	search: '',
	changeAttendanceId: null,
	shouldRefreshTable: true,
	verifiedFilter: '',
	statusFilter: '',
	startDate: '',
	endDate: '',
	pageSize: 10,
};

const usersBreadcrumbs: Array<PageLink> = [
	{
		title: 'Users',
		path: '/users',
		isSeparator: false,
		isActive: false,
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false,
	},
];

const adminsBreadcrumbs: Array<PageLink> = [
	{
		title: 'Admin',
		path: '/admin',
		isSeparator: false,
		isActive: false,
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false,
	},
];

export const UsersForAdmin = () => {
	const [state, dispatch] = useSessionReducer<any, any, string>(reducerFunc, initialState, 'USER');
	const [formLoading, setFormLoading] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);
	const { mode } = useThemeMode();
	const { userModule, currentUser } = useAuth();

	const route = currentUser?.roleName === 'SUPER_ADMIN' ? ADMIN_ROUTE : USERS_ROUTE;
	const location: any = useLocation();

	const handleExportUsers = async () => {
		if (!userModule || !userModule.exportUsers) {
			toast.error('Export functionality is not available.', { id: TOAST_GENERAL });
			return;
		}
		setExportLoading(true);
		toast.info('Starting user export...', { id: 'export-pending', duration: Infinity });

		const params: IParamsInfo = {
			searchKeyword: state.search,
			statusFilter: state.statusFilter === 'EMAIL NOT VERIFIED' ? '' : state.statusFilter,
			verifiedFilter: state.verifiedFilter,
			startDate: state.startDate,
			endDate: state.endDate,
		};

		if (params.verifiedFilter === '1') {
			params.verifiedFilter = 'true';
		} else if (params.verifiedFilter === '0') {
			params.verifiedFilter = 'false';
		}

		const result = await userModule.exportUsers(params);

		setExportLoading(false);
		toast.dismiss('export-pending');

		if (result?.success && result.data instanceof Blob) {
			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(result.data);
			link.download = result.filename || 'users.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(link.href);
			toast.success(result.message || 'Users exported successfully!', { id: TOAST_GENERAL });
		} else {
			toast.error(result?.message || 'Error exporting users.', { id: TOAST_GENERAL });
		}
	};

	let superAminColumns = [
		{
			name: <CommonViewInHeader name='Name' />,
			selector: (row: any) => row.firstName + row.lastName,
			sortable: true,
			width: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						text={row.firstName + ' ' + row.lastName}
						avatar={row?.profilePicture ?? 'media/avatars/blank.png'}
						navigateURL={`${route}/${row.id}`}
						classes='text-dark text-capitalize'
					/>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Email' />,
			selector: (row: any) => row.email,
			width: '240px',
			format: (row: any) => {
				return (
					<CommonRowInTable navigateURL={`${route}/${row.id}`} text={row.email} classes='text-dark' />
				);
			},
			sortable: true,
		},
		{
			name: <CommonViewInHeader name='Date & Time' />,
			selector: (row: any) => row.created_at,
			width: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						navigateURL={`${route}/${row.id}`}
						text={convertUtcToLocal(row.created_at)}
						classes='text-dark text-uppercase'
					/>
				);
			},
			sortable: true,
		},
		{
			name: <CommonViewInHeader name='Address' />,
			sortable: true,
			selector: (row: any) => row.street,
			width: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						navigateURL={`${route}/${row.id}`}
						text={row.street || '-'}
						classes='text-dark text-capitalize'
					/>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Status' />,
			selector: (row: any) => row.status,
			width: '200px',
			format: (row: any) => {
				return row?.deletedAt ? (
					<span className='badge capitalize bg-gray-100 px-6 py-3 text-danger'>Deleted</span>
				) : row.isVerified ? (
					<StatusSelectBox
						value={row.status}
						onChange={(e: any) => {
							updateTUser(row.id, { status: e.target.value });
							e.target.disabled = true;
						}}
					/>
				) : (
					<span className='form-select-type'>Email not verified</span>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Action' />,
			selector: (row: any) => row.email,
			width: '200px',
			format: (row: any) => {
				return (
					<div className='d-flex align-items-center justify-content-center gap-2'>
						<Link title='View user details' className='linkIcon' to={`${route}/${row.id}`}>
							<DiibsIcon icon={Eye} size={24} />
						</Link>
						<Link
							title='Edit user details'
							className={`linkIcon px-3 ${row?.deletedAt ? 'cursor-not-allowed' : ''}`}
							to={row?.deletedAt ? '' : `${route}/${row.id}?edit=true`}
							onClick={() => {
								if (row?.deletedAt) {
									toast.warning('Action not allowed. Admin is deleted.');
								}
							}}
						>
							<DiibsIcon icon={Pencil} size={24} />
						</Link>
						<span
							title='Delete user'
							className={`linkIcon ${row?.deletedAt ? 'cursor-not-allowed' : ''}`}
							onClick={() => {
								if (row?.deletedAt) {
									toast.warning('Action not allowed. Admin is deleted.');
									return;
								}
								dispatch({ type: 'SELECT_ROW', payload: row });
								dispatch({ type: 'OPEN_MODAL', payload: 'DELETE' });
							}}
						>
							<DiibsIcon icon={Trash2} size={24} />
						</span>
					</div>
				);
			},
			sortable: false,
		},
	];
	let userColumns = [
		{
			name: <CommonViewInHeader name='Name ' />,
			selector: (row: any) => row.firstName + row.lastName,
			sortable: true,
			width: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						text={row.firstName + ' ' + row.lastName}
						navigateURL={`${route}/${row.id}`}
						classes='text-dark text-capitalize'
						onClick={() => {
							localStorage.setItem('path', `${route}/${row.id}`);
						}}
					/>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Email' />,
			selector: (row: any) => row.email,
			width: '240px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						navigateURL={`${route}/${row.id}`}
						text={row.email}
						classes='text-dark'
						onClick={() => {
							localStorage.setItem('path', `${route}/${row.id}`);
						}}
					/>
				);
			},
			sortable: true,
		},
		{
			name: <CommonViewInHeader name='Mobile Number' />,
			selector: (row: any) => row?.mobileNumber,
			minWidth: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						text={
							row?.mobileNumber !== null
								? `${row.mobileCountryCode?.startsWith('+') ? '' : '+'}${row.mobileCountryCode}${row.mobileNumber
								}`
								: '-'
						}
						classes='text-theme-color text-dark'
					/>
				);
			},
			sortable: true,
			sortCompare: (a: any, b: any) => a - b,
		},
		{
			name: <CommonViewInHeader name='Address' />,
			sortable: true,
			selector: (row: any) => row.city,
			width: '200px',
			format: (row: any) => {
				return (
					<CommonRowInTable
						navigateURL={`${route}/${row.id}`}
						text={row.city || '-'}
						classes='text-dark text-capitalize'
						onClick={() => {
							localStorage.setItem('path', `${route}/${row.id}`);
						}}
					/>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Status' />,
			selector: (row: any) => row.status,
			width: '200px',
			format: (row: any) => {
				return row?.deletedAt ? (
					<span className='badge capitalize bg-gray-100 px-6 py-3 text-danger'>Deleted</span>
				) : row.isVerified ? (
					<StatusSelectBox
						value={row.status}
						onChange={(e: any) => {
							updateTUser(row.id, { status: e.target.value });
							e.target.disabled = true;
						}}
					/>
				) : (
					<span className='form-select-type'>Email not verified</span>
				);
			},
		},
		{
			name: <CommonViewInHeader name='Action' />,
			selector: (row: any) => row.email,
			width: '200px',
			format: (row: any) => {
				return (
					<div className='d-flex align-items-center justify-content-center gap-2'>
						<Link title='View user details' className='linkIcon' to={`${route}/${row.id}`}>
							<DiibsIcon icon={Eye} size={24} />
						</Link>
						<Link
							title='Edit user details'
							className={`linkIcon px-3 ${row?.deletedAt ? 'cursor-not-allowed' : ''}`}
							to={row?.deletedAt ? '' : `${route}/${row.id}?edit=true`}
							onClick={() => {
								if (row?.deletedAt) {
									toast.warning('Action not allowed. User is deleted.');
								} else {
									localStorage.setItem('path', `${route}/${row.id}`);
								}
							}}
						>
							<DiibsIcon icon={Pencil} size={24} />
						</Link>
						<span
							title='Delete user'
							className={`linkIcon ${row?.deletedAt ? 'cursor-not-allowed' : ''}`}
							onClick={() => {
								if (row?.deletedAt) {
									toast.warning('Action not allowed. User is deleted.');
									return;
								}
								dispatch({ type: 'SELECT_ROW', payload: row });
								dispatch({ type: 'OPEN_MODAL', payload: 'DELETE' });
							}}
						>
							<DiibsIcon icon={Trash2} size={24} />
						</span>
					</div>
				);
			},
			sortable: false,
		},
	];

	const columns = currentUser?.roleName === 'SUPER_ADMIN' ? superAminColumns : userColumns;

	const getTUsers = async () => {
		dispatch({ type: 'LOADING', payload: true });

		const result = await userModule?.getUsers({
			pageNo: state.currentPage ? state.currentPage : 1,
			pageSize: state.pageSize,
			verifiedFilter:
				currentUser?.roleName === 'ADMIN'
					? '1'
					: state.statusFilter === 'EMAIL NOT VERIFIED'
						? '0'
						: state.statusFilter || location?.state?.value
							? '1'
							: '',
			statusFilter:
				state.statusFilter === 'EMAIL NOT VERIFIED'
					? ''
					: state.statusFilter
						? state.statusFilter
						: location?.state?.value,
			searchKeyword: state.search,
			startDate: state.startDate,
			endDate: state.endDate,
		});
		if (result?.success) {
			dispatch({ type: 'GET_DATA', payload: result?.data?.rows || [] });
			dispatch({ type: 'SET_TOTAL_PAGES', payload: result?.data?.count || 1 });
		} else {
			dispatch({ type: 'GET_DATA', payload: [] });
			dispatch({ type: 'SET_TOTAL_PAGES', payload: 1 });

			if (result?.status !== 600) {
				toast.error(result?.message || API_ERROR_MESSAGE, {
					id: TOAST_GENERAL,
				});
			}
		}
		dispatch({ type: 'LOADING', payload: false }); // Ensure loading stops even on error
	};

	const createTUser = async (userData: any) => {
		setFormLoading(true);

		const result = await userModule?.createUser({ ...userData, roleName: 'ADMIN' });
		setFormLoading(false);
		if (result?.success) {
			toast.success(`Admin created successfully, credentials sent to email.`, {
				id: TOAST_GENERAL,
			});
			getTUsers();
			return true;
		} else {
			toast.error(result?.message || `Problem occured, While creating Admin`, {
				id: TOAST_GENERAL,
			});
			return false;
		}
	};

	const updateTUser = async (userId: any, userData: any) => {
		setFormLoading(true);
		const result = await userModule?.updateUser(userId, userData);
		if (result?.success) {
			toast.success(`User Updated succesfully`, {
				id: 'user updated',
			});
			getTUsers();
		} else {
			toast.error(result?.message || API_ERROR_MESSAGE, {
				id: TOAST_GENERAL,
			});
			getTUsers(); // Refresh even on error to potentially revert changes in UI
		}
		setFormLoading(false);
	};

	const deleteTUser = async (userIds: any) => {
		dispatch({ type: 'CLOSE_MODAL' });
		const result = await userModule?.deleteUsers(userIds);
		if (result?.success) {
			toast.success(`${currentUser?.roleName === 'SUPER_ADMIN' ? 'Admin' : 'User'} Deleted succesfully`, {
				id: 'user deleted',
			});
			getTUsers();
		} else {
			toast.error(result?.message || API_ERROR_MESSAGE, {
				id: TOAST_GENERAL,
			});
		}
	};

	useEffect(() => {
		getTUsers();
	}, [
		state.currentPage,
		state.pageSize,
		state.verifiedFilter,
		state.search,
		state.statusFilter,
		state.startDate,
		state.endDate,
		location?.state?.value,
	]);

	const deleteItem = function (rows: any) {
		dispatch({ type: 'SELECT_ROW', payload: rows });
		dispatch({ type: 'OPEN_MODAL', payload: 'DELETE' });
	};

	const tableToolbarActions = (
		<div className='d-flex align-items-center gap-2'>
			{currentUser?.roleName === 'SUPER_ADMIN' && (
				<button
					type='button'
					className='btn btn-sm btn-primary'
					onClick={() => {
						dispatch({ type: 'OPEN_MODAL', payload: 'USER' });
					}}
				>
					<DiibsIcon icon={Plus} size={20} />
					Añadir Admin
				</button>
			)}
			<button
				type='button'
				className='btn btn-sm btn-light-primary'
				onClick={handleExportUsers}
				disabled={exportLoading || state.loading}
			>
				{exportLoading ? (
					<>
						Exporting...
						<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
					</>
				) : (
					<>
						<DiibsIcon icon={Upload} size={20} className='me-1' />
						Export List
					</>
				)}
			</button>
		</div>
	);

	let filters: any = {
		label: 'Status',
		options: ['Active', 'Inactive', 'Deleted'].map((t) => ({
			value: t.toUpperCase(),
			label: t,
		})),
		selected: location?.state,
	};

	if (currentUser?.roleName === 'SUPER_ADMIN') {
		filters = [
			{
				label: 'Status',
				key: 'Status', // Use key for referencing in filtersCallback
				options: ['Active', 'Inactive', 'Deleted'].map((t) => ({
					value: t.toUpperCase(),
					label: t,
				})),
			},
			{
				label: 'Email verified',
				key: 'Email verified', // Use key for referencing in filtersCallback
				options: [
					{ value: 'true', label: 'Verified' },
					{ value: 'false', label: 'Not Verified' },
				],
			},
		];
	} else {
		filters = [filters]; // Wrap in array if SUPER_ADMIN adds more filters
	}

	return (
		<>
			<PageTitle>{currentUser?.roleName === 'ADMIN' ? 'Users List' : 'Admin List'}</PageTitle>
			<DataTableWrapper
				totalData={state.totalData}
				subHeader={tableToolbarActions}
				selectableRows={true}
				loading={state.loading}
				columns={columns}
				data={state.data}
				handleSelected={deleteItem}
				currentPage={state.currentPage}
				totalPages={state.totalPages}
				pageSize={state.pageSize}
				handlePageChange={(page) => dispatch({ type: 'PAGE_CHANGE', payload: page })}
				handlePageSizeChange={(pageSize) => dispatch({ type: 'SET_PAGE_SIZE', payload: +pageSize })}
				handleSearch={(searchVal) => {
					dispatch({ type: 'SEARCH_CHANGE', payload: searchVal });
				}}
				searchValue={state.search}
				filters={{
					dropdown: filters, // Pass the array of filter configurations
					date: currentUser?.roleName === 'SUPER_ADMIN',
					preserveType: 'USER',
				}}
				filtersCallback={(values: any) => {
					dispatch({
						type: 'SET_FILTER',
						payload: {
							statusFilter: values?.Status ? values.Status.value : '',
							verifiedFilter: values['Email verified'] ? values['Email verified'].value : '',
						},
					});
					dispatch({
						type: 'DATE_FILTER',
						payload: { startDate: values.startDate, endDate: values.endDate },
					});
				}}
				isFilterApplied={Boolean(
					state.search ||
					state.statusFilter ||
					state.startDate ||
					state.endDate ||
					state.verifiedFilter
				)}
			/>
			{state.isModalOpen && (
				<CustomModal
					data={state.selectedRow}
					handleCancelCallback={() => dispatch({ type: 'CLOSE_MODAL' })}
					handleSaveCallback={async (value: any) => {
						if (state.modalType === 'DELETE') {
							const ids = Array.isArray(state.selectedRow)
								? state.selectedRow.map((row: any) => row.id)
								: [state.selectedRow.id];
							deleteTUser({ ids: ids });
						} else if (state.modalType === 'USER') { // Assuming 'USER' is for Add Admin
							const result = await createTUser(value);
							if (result) dispatch({ type: 'CLOSE_MODAL' });
							else return false;
						}
					}}
					title={
						currentUser?.roleName === 'ADMIN'
							? 'Delete User'
							: state.modalType === 'DELETE'
								? 'Delete Admin'
								: 'Add Admin'
					}
					type={state.modalType}
				/>
			)}
		</>
	);
};